import {useCallback, useEffect, useRef, useState} from "react";

export const SPEED_OPTIONS = [0.85, 1.0, 1.2];

/**
 * Formats a duration in seconds into MM:SS format.
 * Handles null, NaN, and negative numbers defensively.
 * @param {number} seconds
 * @returns {string}
 */
export function formatAudioTime(seconds) {
    if (seconds == null || isNaN(seconds) || seconds < 0) {
        return "00:00";
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const paddedMins = String(mins).padStart(2, "0");
    const paddedSecs = String(secs).padStart(2, "0");
    return `${paddedMins}:${paddedSecs}`;
}

/**
 * Validates audio URL according to KulturIT security guidelines:
 * Only relative same-origin paths or explicit HTTPS protocols are permitted.
 * @param {string} url
 * @returns {boolean}
 */
export function isValidAudioUrl(url) {
    if (!url || typeof url !== "string") return false;
    const trimmed = url.trim();
    if (trimmed.startsWith("//") || trimmed.startsWith("/\\")) return false;
    return trimmed.startsWith("/") || trimmed.startsWith("./") || /^https:\/\//i.test(trimmed);
}

/**
 * Custom hook managing HTML5 audio playback, buffering, pacing, and safe cleanup.
 */
export function useAudioPlayback({src, title}) {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1.0);
    const [isMuted, setIsMuted] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [liveAnnouncement, setLiveAnnouncement] = useState("");

    const isUrlSafe = isValidAudioUrl(src);

    useEffect(() => {
        if (!isUrlSafe && src) {
            setErrorMessage("Ugyldig eller usikker lydkilde. Kun HTTPS eller lokale lydfiler støttes.");
        } else {
            setErrorMessage(null);
        }
    }, [src, isUrlSafe]);

    // Reset playback position and playing state when track source changes
    useEffect(() => {
        const audio = audioRef.current;
        if (audio && typeof audio.pause === "function") {
            audio.pause();
        }
        setCurrentTime(0);
        setDuration(0);
        setIsPlaying(false);
    }, [src]);

    // Ensure audio pauses on component unmount
    useEffect(() => {
        const audio = audioRef.current;
        return () => {
            if (audio && typeof audio.pause === "function") {
                audio.pause();
            }
        };
    }, []);

    const togglePlay = useCallback(() => {
        const audio = audioRef.current;
        if (!audio || !isUrlSafe) return;

        if (isPlaying) {
            audio.pause();
        } else {
            audio.play().catch(() => {
                setErrorMessage("Kunne ikke spille av lydfilen. Sjekk nettverket ditt.");
            });
        }
    }, [isPlaying, isUrlSafe]);

    const handleSkip = useCallback((deltaSeconds) => {
        const audio = audioRef.current;
        if (!audio) return;
        const newTime = Math.max(0, Math.min(audio.currentTime + deltaSeconds, audio.duration || 0));
        audio.currentTime = newTime;
        setCurrentTime(newTime);
        const direction = deltaSeconds < 0 ? "tilbake" : "frem";
        setLiveAnnouncement(`Hoppet 15 sekunder ${direction}`);
    }, []);

    const handleSpeedChange = useCallback((rate) => {
        const audio = audioRef.current;
        if (audio) {
            audio.playbackRate = rate;
            if ("preservesPitch" in audio) {
                audio.preservesPitch = true;
            }
        }
        setPlaybackRate(rate);
        setLiveAnnouncement(`Avspillingshastighet satt til ${rate}x`);
    }, []);

    const handleToggleMute = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.muted = !isMuted;
        setIsMuted(!isMuted);
    }, [isMuted]);

    const handleSeek = useCallback((event, newValue) => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.currentTime = newValue;
        setCurrentTime(newValue);
    }, []);

    // HTML5 native event listeners for resilient synchronization
    const onPlay = useCallback(() => {
        setIsPlaying(true);
        setLiveAnnouncement(`Spiller av ${title || "meditasjonslyd"}`);
    }, [title]);

    const onPause = useCallback(() => {
        setIsPlaying(false);
        setLiveAnnouncement("Lydavspilling satt på pause");
    }, []);

    const onEnded = useCallback(() => {
        setIsPlaying(false);
        setCurrentTime(0);
        setLiveAnnouncement("Avspilling fullført");
    }, []);

    const onLoadedMetadata = useCallback((e) => {
        setDuration(e.currentTarget.duration || 0);
    }, []);

    const onTimeUpdate = useCallback((e) => {
        setCurrentTime(e.currentTarget.currentTime || 0);
    }, []);

    const onError = useCallback(() => {
        setErrorMessage("Kunne ikke laste lydfilen. Vennligst sjekk nettverkstilkoblingen.");
    }, []);

    return {
        audioRef,
        isUrlSafe,
        isPlaying,
        currentTime,
        duration,
        playbackRate,
        isMuted,
        errorMessage,
        liveAnnouncement,
        togglePlay,
        handleSkip,
        handleSpeedChange,
        handleToggleMute,
        handleSeek,
        audioHandlers: {
            onPlay,
            onPause,
            onEnded,
            onLoadedMetadata,
            onTimeUpdate,
            onError,
        },
    };
}
